<template lang="pug">
.page.page-dialogues
  rail-layout
    template(#side)
      h1.dialogues-title {{ $t('dialogues.title') }}
      p.dialogues-subtitle {{ $t('dialogues.subtitle') }}
      p.locale-notice(v-if="!isZh") {{ $t('dialogues.zhOnlyNotice') }}
      details.dialogue-background
        summary {{ isZh ? '參與者與資料說明' : 'Participants & record information' }}
        p.intro {{ $t('dialogues.intro') }}
        .speakers(v-if="speakers.length")
          .section-heading {{ $t('dialogues.whoSpeaks') }}
          template(v-for="sp in speakers", :key="sp.id")
            a.speaker-card(v-if="sp.external", :href="sp.href", target="_blank", rel="noopener", :class="'speaker-' + sp.id")
              span.speaker-name {{ sp.name }}
              span.speaker-role {{ sp.role }}
              span.speaker-stats {{ sp.stats }}
            router-link.speaker-card(v-else, :to="sp.href", :class="'speaker-' + sp.id")
              span.speaker-name {{ sp.name }}
              span.speaker-role {{ sp.role }}
              span.speaker-stats {{ sp.stats }}
        details.conventions
          summary.section-heading {{ $t('dialogues.conventionsTitle') }}
          ul
            li(v-for="(line, i) in conventions", :key="'conv-' + i") {{ line }}
        .side-links
          router-link.pill(:to="isZh ? '/zh/thinking/' : '/thinking/'") ← {{ $t('nav.thinking') }}
          a.pill(href="/dialogues/index.json", target="_blank", rel="noopener") index.json
          a.pill(href="/feed.xml", target="_blank", rel="noopener") Atom

    .dialogues-main
      .dialogue-search
        label(for="dialogue-search") {{ isZh ? '搜尋對話與思想' : 'Search dialogues & ideas' }}
        .dialogue-search-row
          input#dialogue-search(type="search", v-model="query", :placeholder="isZh ? '標題、主題或說話者' : 'Title, topic, or participant'")
          button(type="button", v-if="query", @click="clearSearch") {{ isZh ? '清除' : 'Clear' }}
        p.search-count(aria-live="polite") {{ filteredDialogues.length }} / {{ dialogues.length }} {{ isZh ? '篇紀錄' : 'records' }}
      .dialogue-list(v-if="filteredDialogues.length")
        router-link.dialogue-card(
          v-for="d in filteredDialogues",
          :key="d.slug",
          :to="detailPath(d.slug)"
        )
          .card-meta
            span.no(v-if="d.no") No. {{ d.no }}
            span.dot(v-if="d.no") ·
            span.date {{ d.date }}
            span.dot(v-if="pick(d.occasion)") ·
            span.occasion(v-if="pick(d.occasion)") {{ pick(d.occasion) }}
            span.dot(v-if="d.rounds") ·
            span.language(v-if="!d.available_in.includes(currentLang)") {{ d.available_in.includes('zh') ? '中文' : 'English' }}
            span.format(v-if="d.format === 'edited-notes'") {{ $t('dialogues.editedNotes') }}
            span.rounds(v-if="d.rounds") {{ d.rounds }} {{ $t('dialogues.roundsUnit') }}
            span.stage.tag(v-if="d.health", :class="'stage-' + d.health.stage", :title="stageTitle(d.health)") {{ stageLabel(d.health.stage) }}
          h2.card-title {{ pick(d.title) }}
          p.card-subtitle(v-if="pick(d.subtitle)") {{ pick(d.subtitle) }}
          p.card-summary(v-if="pick(d.summary)") {{ pick(d.summary) }}
          .card-foot
            span.participants
              span.participant(v-for="p in d.participants", :key="p.id", :class="'participant-' + p.id")
                span.p-name {{ pick(p.name) }}
            span.read-more {{ $t('dialogues.readDialogue') }} →
      p.empty(v-else) {{ query ? (isZh ? '沒有符合的紀錄，請試試其他關鍵字。' : 'No matching records. Try another keyword.') : $t('dialogues.empty') }}
</template>

<script>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'
import { ogTitle } from '@/runtime/page-title.mjs'
import dialogues from '@/data/dialogues.json'

export default {
  name: 'PageDialogues',
  setup() {
    const { t, locale } = useI18n()
    const route = useRoute()
    useHead({
      title: computed(() => t('dialogues.title')),
      meta: computed(() => [
        { name: 'description', content: t('dialogues.subtitle') },
        { property: 'og:title', content: ogTitle(t('dialogues.title'), locale.value) },
        { property: 'og:description', content: t('dialogues.subtitle') },
      ]),
      link: computed(() => [{ rel: 'canonical', href: 'https://cheyuwu.com' + (route.path.endsWith('/') ? route.path : route.path + '/') }]),
    })
    return { t, locale }
  },
  data() {
    return { dialogues, query: '' }
  },
  computed: {
    filteredDialogues() {
      const q = this.query.trim().toLocaleLowerCase()
      return this.dialogues.filter(d => [this.pick(d.title), this.pick(d.subtitle), this.pick(d.summary), this.pick(d.occasion), d.date, ...d.participants.map(p => this.pick(p.name))].join(' ').toLocaleLowerCase().includes(q))
    },
    conventions() { return this.$tm('dialogues.conventions') || [] },
    isZh() { return this.$route.path.startsWith('/zh') || this.locale === 'zh' },
    currentLang() { return this.isZh ? 'zh' : 'en' },
    // Speaker cards, SayIt-style: who speaks here, with counts across all published dialogues
    speakers() {
      const people = new Map()
      for (const d of this.dialogues) for (const p of d.participants) {
        if (!people.has(p.id)) people.set(p.id, { ...p, count: 0 })
        people.get(p.id).count++
      }
      return [...people.values()].map(p => ({
        id: p.id, name: this.pick(p.name), role: this.pick(p.role),
        stats: this.isZh ? `${p.count} 篇紀錄` : `${p.count} records`,
        href: p.url || (this.isZh ? '/zh/about/' : '/about/'), external: !!p.url,
      }))
    },
  },
  methods: {
    // 透明度階段（scripts/lib/dialogue-health.mjs），同 Taiwan.md 文章卡的 🌱 進化中 pill
    stageLabel(stage) {
      const zh = { seed: '🌱 育種期', growing: '🌿 生長期', mature: '🌳 成熟' }
      const en = { seed: '🌱 Seedling', growing: '🌿 Growing', mature: '🌳 Mature' }
      return (this.isZh ? zh : en)[stage] || ''
    },
    stageTitle(h) { return this.isZh ? `哲宇原話 ${h.cheyu}%・健檢 ${h.score}` : `Che-Yu's own words ${h.cheyu}% · health ${h.score}` },
    clearSearch() {
      this.query = ''
      this.$nextTick(() => this.$el.querySelector('#dialogue-search')?.focus())
    },
    pick(value) {
      if (value == null) return ''
      if (typeof value === 'string') return value
      return value[this.currentLang] || value.zh || value.en || ''
    },
    detailPath(slug) {
      const d = this.dialogues.find(d => d.slug === slug)
      const lang = d?.available_in.includes(this.currentLang) ? this.currentLang : d?.available_in[0]
      return `${lang === 'zh' ? '/zh' : ''}/dialogues/${slug}/`
    },
  },
}
</script>

<style lang="sass">
.page-dialogues
  background-color: #000
  color: #fff
  min-height: 100vh
  text-align: left

  .dialogues-title
    font-family: var(--font-display)
    font-size: 2.4rem
    font-weight: 600
    letter-spacing: 0.02em
    margin: 0
  .dialogues-subtitle
    font-size: 1rem
    color: #aaa
    line-height: 1.7
    margin: 0

  .speakers
    border-top: 1px solid var(--line)
    padding-top: 14px
    display: flex
    flex-direction: column
    gap: 8px
  .speaker-card
    display: flex
    flex-direction: column
    gap: 3px
    padding: 12px 16px
    border: 0
    border-radius: var(--r-md)
    background: var(--tint-1)
    color: var(--ink)
    text-decoration: none
    transition: var(--t-hover)
    &:hover
      background: var(--tint-2)
      text-decoration: none
    .speaker-name
      font-weight: 600
      letter-spacing: 0.04em
    .speaker-role
      font-size: 0.8rem
      color: var(--ink-3)
    .speaker-stats
      font-size: 0.82rem
      color: #9a9a96
      margin-top: 2px
  .speaker-cheyu .speaker-name
    color: var(--gold)
  .speaker-muse .speaker-name
    color: #b9a6dd

  .conventions
    border-top: 1px solid var(--line)
    padding-top: 14px
    color: #9a9a96
    font-size: 0.88rem
    summary
      cursor: pointer
      list-style: none
      &::-webkit-details-marker
        display: none
      &::after
        content: ' ▾'
        color: #666
    ul
      margin: 8px 0 0
      padding-left: 1.2em
      line-height: 1.7
      li
        margin-bottom: 6px

  .side-links
    display: flex
    flex-wrap: wrap
    gap: 8px
    border-top: 1px solid var(--line)
    padding-top: 14px

  .intro
    font-size: 1rem
    line-height: 1.85
    color: #bbb
    max-width: 720px
    margin: 6px 0 26px

  .dialogue-list
    display: flex
    flex-direction: column
    gap: 18px

  .dialogue-card
    display: block
    padding: 26px 28px
    border: 0
    border-radius: var(--r-lg)
    background: var(--bg1)
    color: var(--ink)
    text-decoration: none
    transition: var(--t-hover)
    &:hover
      background: var(--bg2)
      text-decoration: none
      .card-title, .read-more
        color: var(--gold)
    .card-meta
      font-size: 0.8rem
      color: var(--ink-3)
      letter-spacing: 0.08em
      text-transform: uppercase
      margin-bottom: 12px
      .dot
        margin: 0 8px
        opacity: 0.5
      // 階段：共用 .tag（色點＋文字）
      .stage
        margin-left: 10px
        --tag-c: var(--k-sketch)
        letter-spacing: 0.02em
        text-transform: none
      .stage-mature
        --tag-c: var(--gold)
    .card-title
      font-family: var(--font-display)
      font-size: 1.55rem
      font-weight: 600
      line-height: 1.35
      margin-bottom: 8px
    .card-subtitle
      font-size: 1rem
      color: #aaa
      margin-bottom: 12px
    .card-summary
      font-size: 0.95rem
      color: #8f8f8f
      line-height: 1.75
      margin-bottom: 18px
    .card-foot
      display: flex
      justify-content: space-between
      align-items: center
      gap: 16px
      font-size: 0.85rem
    .participants
      display: inline-flex
      gap: 8px
    .read-more
      color: #888
      transition: var(--t-hover)
      white-space: nowrap

  .empty
    color: #777

  @media (max-width: 991px)
    .dialogues-title
      font-size: 1.9rem
</style>

<style scoped>
.dialogue-background > summary { cursor:pointer; min-height:44px; padding:10px 0; color:var(--ink-2); }
.dialogue-background > summary:focus-visible, .dialogue-search button:focus-visible, .dialogue-search input:focus-visible { outline:2px solid var(--gold); outline-offset:4px; }
.dialogue-search { margin-bottom:24px; }
.dialogue-search label { display:block; margin-bottom:10px; color:var(--ink-2); }
.dialogue-search-row { display:flex; gap:12px; }
.dialogue-search input { min-width:0; flex:1; min-height:44px; padding:10px 18px; background:var(--bg2); border:0; border-radius:var(--r-pill); color:var(--ink); }
.dialogue-search input:focus:not(:focus-visible) { outline:none; box-shadow:0 0 0 1px var(--gold); }
.dialogue-search button { min-height:44px; padding:10px 20px; background:var(--tint-2); color:var(--ink-2); border:0; border-radius:var(--r-pill); cursor:pointer; }
.dialogue-search button:hover { background:var(--tint-3); color:var(--ink); }
.search-count { margin:12px 0 0; color:var(--ink-2); font-size:13px; }
@media(max-width:991px) { .dialogue-card { padding:20px; } .dialogue-card .participants { flex-wrap:wrap; } .dialogue-card .card-foot { flex-wrap:wrap; } }
</style>
